const LOCAL_STORAGE_KEY = "compareProducts";

const selectedProducts = new Map(
  JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || [],
);
const sectionCache = new Map();

const setLocalStorage = debounce(() => {
  const json = JSON.stringify(Array.from(selectedProducts.entries()));
  localStorage.setItem(LOCAL_STORAGE_KEY, json);
}, 200);

const createImageElement = (src, alt) => {
  const imgElement = document.createElement("img");
  imgElement.setAttribute("src", src);
  imgElement.setAttribute("alt", alt);
  imgElement.setAttribute("loading", "lazy");
  imgElement.classList.add("compare-image");
  return imgElement;
};

function createImagePlaceholder(index, onClick) {
  const placeholderDiv = document.createElement("div");
  placeholderDiv.setAttribute("data-compare-image-placeholder", index);
  if (typeof onClick === "function") placeholderDiv.onclick = onClick;
  const icon = document.createElement("img");
  icon.setAttribute(
    "src",
    "https://www.nudestix.com/cdn/shop/t/541/assets/icon-plus.svg?v=123710698321474051491727285590",
  );
  icon.setAttribute("alt", "Add Product");
  icon.setAttribute("width", "34");
  icon.setAttribute("height", "34");
  icon.classList.add("compare-image-placeholder");
  placeholderDiv.appendChild(icon);
  return placeholderDiv;
}

const createBvInlineRating = (productId) => {
  const bvInlineRating = document.createElement("div");
  bvInlineRating.setAttribute("data-bv-show", "inline_rating");
  bvInlineRating.setAttribute("data-bv-product-id", productId);
  bvInlineRating.setAttribute("data-bv-redirect-url", "");
  bvInlineRating.setAttribute("data-bv-seo", "false");
  bvInlineRating.setAttribute("tabindex", "0");
  return bvInlineRating;
};

const getNumber = (number, defaultValue = 0) => {
  const value = Number(number);
  return isNaN(value) ? defaultValue : value;
};

async function fetchProductDesc(productHandle) {
  try {
    const response = await fetch(
      `/products/${productHandle}?sections=main-product`,
    );
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    const mainProduct = data["main-product"];
    const html = new DOMParser().parseFromString(mainProduct, "text/html");

    const whatItIsJson = html.querySelector(
      'script[type="application/json"][data-what-it-is]',
    );
    const whatItDoesJson = html.querySelector(
      'script[type="application/json"][data-what-it-does]',
    );

    return {
      whatItIs: whatItIsJson ? JSON.parse(whatItIsJson.textContent) : null,
      whatItDoes: whatItDoesJson
        ? JSON.parse(whatItDoesJson.textContent)
        : null,
    };
  } catch (error) {
    console.error("Error fetching product description:", error);
    return {
      whatItIs: null,
      whatItDoes: null,
    };
  }
}

const navbarToggle = (toggle) => {
  const header = document.getElementById("shopify-section-header");
  if (!header) return;
  const classesToToggle = [
    "scrolled-past-header",
    "shopify-section-header-sticky",
    "animate",
    "shopify-section-header-hidden",
  ];

  header.classList[toggle ? "add" : "remove"](...classesToToggle);
};

const updateCompareDrawer = debounce(() => {
  const drawer = document.querySelector("product-compare-drawer");
  if (drawer) drawer.updateDrawer();
  else console.warn("Product compare drawer not found");
}, 100);

const updateCompareTable = debounce(() => {
  const table = document.querySelector("product-compare-table");
  if (table) table.updateProduct();
  else console.warn("Product compare table not found");
}, 100);

const updateCheckboxInput = (id, checked) => {
  const input = document.querySelector(
    `product-compare-checkbox input[value="${id}"]`,
  );
  if (input) input.checked = checked;
};

function updateCompareVariant(variantId, productId) {
  const input = document.querySelector(
    `product-compare-checkbox input[value="${productId}"]`,
  );
  if (input) input.setAttribute("data-variant-id", variantId);
  if (selectedProducts.has(productId)) {
    const product = selectedProducts.get(productId);
    product.variantId = variantId;
    selectedProducts.set(productId, product);
    setLocalStorage();
  }
}

class ProductCompareCheckbox extends HTMLElement {
  constructor() {
    super();
    this.limit = getNumber(this.dataset.compareLimit);
    this.input = this.querySelector("input");
  }

  connectedCallback() {
    this.input.addEventListener("change", this.handleInputChange.bind(this));
    this.restoreCheckedInputs();
  }

  handleInputChange(event) {
    try {
      const productData = this.getProductData(event.target);

      if (
        event.target.checked &&
        (this.isLimitReached() || this.isDifferentCategory(productData))
      ) {
        event.target.checked = false;
        return;
      }

      event.target.checked
        ? selectedProducts.set(productData.id, productData)
        : selectedProducts.delete(productData.id);
      this.updateUI();
    } catch (error) {
      console.error("Error handling input change:", error);
    }
  }

  updateUI() {
    updateCompareDrawer();
    setLocalStorage();
  }

  isDifferentCategory(productData) {
    if (selectedProducts.size === 0) return false;
    const [firstProduct] = selectedProducts.values();
    const isDifferent = productData.productType !== firstProduct.productType;
    if (isDifferent) this.showAlert("You can only compare similar products");
    return isDifferent;
  }

  isLimitReached() {
    const isReached = selectedProducts.size >= this.limit;
    if (isReached)
      this.showAlert(`You have already selected ${this.limit} products`);
    return isReached;
  }

  showAlert(message) {
    let element = document.querySelector("[data-warn-popup2]");
    if (element) {
      element.classList.remove("hidden");
      let alertMessage = element.querySelector(".warning-content p");
      if (alertMessage) alertMessage.innerHTML = message;
    }
  }

  restoreCheckedInputs() {
    selectedProducts.forEach((_, productId) => {
      const input = this.querySelector(`input[value="${productId}"]`);
      if (input) input.checked = true;
    });
  }

  getPrice(priceId) {
    const priceContainer = document.getElementById(priceId);
    if (!priceContainer) {
      console.error(`Price container with id ${priceId} not found`);
      return "$00.00";
    }
    const priceElement =
      priceContainer.querySelector(".price-item--regular") ||
      priceContainer.querySelector(".price-item--sale span.money");
    return priceElement ? priceElement.textContent.trim() : "$00.00";
  }

  getProductData(el) {
    return {
      id: el.dataset.compId,
      variantId: el.dataset.variantId,
      handle: el.dataset.compHandle,
      productType: el.dataset.compProductType,
      image: el.dataset.compImage,
      title: el.dataset.compTitle,
      priceId: el.dataset.compPriceId,
      price: this.getPrice(el.dataset.compPriceId),
      finish: el.dataset.compFinish,
      skinType: el.dataset.compSkinType,
      benefits: el.dataset.compBenefits,
      formulated: el.dataset.compFormulated,
      features: el.dataset.compFeatures,
      shadeRange: el.dataset.compShadeRange,
      available: el.dataset.compAvailable,
    };
  }
}

if (!customElements.get("product-compare-checkbox")) {
  customElements.define("product-compare-checkbox", ProductCompareCheckbox);
}

class ProductCompareDrawer extends HTMLElement {
  constructor() {
    super();
    this.toggle = this.querySelector("[data-compare-toggle]");
    this.products = this.querySelector("[data-compare-products]");
    this.limit = getNumber(this.dataset.compareLimit);
    this.compareButtons = this.querySelectorAll("[data-compare-open]");
    this.clearButtons = this.querySelectorAll("[data-compare-clear]");
  }

  connectedCallback() {
    this.compareButtons.forEach((button) =>
      button.addEventListener("click", this.handleCompare.bind(this)),
    );
    this.clearButtons.forEach((button) =>
      button.addEventListener("click", this.clearAll.bind(this)),
    );
    this.setupDeleteProductListeners();
    this.setupDrawerToggle();
    if (selectedProducts.size > 0) this.updateDrawer();
  }

  setupDrawerToggle() {
    if (!this.toggle) {
      console.error("Drawer toggle element not found");
      return;
    }
    this.toggle.addEventListener("click", () => {
      const isOpen = this.products.classList.toggle("hidden");
      this.toggle.setAttribute("aria-expanded", !isOpen);
      this.toggle.classList.toggle("active", !isOpen);
      navbarToggle(!isOpen);
    });
  }

  async handleCompare(e) {
    this.setCompareButtonState(true, "Loading...");

    try {
      await this.fetchProductDescriptions();
      this.showModal(e.target.dataset.modal);
      updateCompareTable();
    } catch (error) {
      console.error("Error fetching How to Use content:", error);
    } finally {
      this.setCompareButtonState(false, "Compare Products");
    }
  }

  setCompareButtonState(isDisabled, text) {
    this.compareButtons.forEach((button) => {
      button.disabled = isDisabled;
      button.textContent = text;
    });
  }

  async fetchProductDescriptions() {
    const fetchPromises = Array.from(selectedProducts.values())
      .filter(
        (product) =>
          !sectionCache.has(product.id) &&
          product?.handle &&
          product?.productType,
      )
      .map(async (product) => {
        try {
          const sectionContent = await fetchProductDesc(
            product.handle,
            product.productType,
          );
          sectionCache.set(product.id, {
            whatItDoes: sectionContent.whatItDoes || "N/A",
            whatItIs: sectionContent.whatItIs || "N/A",
          });
        } catch (error) {
          console.error(
            `Error fetching desc for product ${product.id}:`,
            error,
          );
        }
      });

    await Promise.allSettled(fetchPromises);
  }

  showModal(modalSelector) {
    navbarToggle(true);
    const modal = document.querySelector(modalSelector);
    if (modal) modal.show(this.compareButton);
  }

  updateDrawer() {
    this.drawerVisibility();
    this.updateCounters();
    this.updateProductSlots();
  }

  drawerVisibility() {
    const isHidden = selectedProducts.size === 0;
    this.classList.toggle("hidden", isHidden);
    if (isHidden) {
      this.products.classList.add("hidden");
      this.toggle.setAttribute("aria-expanded", false);
      this.toggle.classList.remove("active");
    }
  }

  updateCounters() {
    const selectedCount = this.querySelector("[data-compare-selected-count]");
    const requiredCount = this.querySelector("[data-compare-required-count]");

    if (selectedCount) {
      selectedCount.innerHTML = `Compare products <span class="selected-count">(${selectedProducts.size} OF ${this.limit})</span>`;
    }

    if (requiredCount) {
      requiredCount.innerHTML =
        selectedProducts.size === this.limit
          ? "You have selected the maximum number of products for comparison"
          : `You must select at least <span class="required-count">${
              this.limit - selectedProducts.size
            } products</span> to compare`;
    }
  }

  updateProductSlots() {
    const products = Array.from(selectedProducts.values());
    for (let i = 0; i < this.limit; i++) {
      products[i]
        ? this.populateProduct(i, products[i])
        : this.removeProduct(i);
    }
  }

  populateProduct(index, product) {
    const element = this.querySelector(`[data-compare-index="${index}"]`);
    if (element) {
      element.setAttribute("data-compare-empty", "false");
      const newImage = createImageElement(product.image, product.title);
      const imageContainer = element.querySelector("[data-compare-image]");
      if (imageContainer) imageContainer.replaceChildren(newImage);
      const titleElement = element.querySelector("[data-compare-title]");
      if (titleElement) titleElement.textContent = product.title;
    }
  }

  removeProduct(index) {
    const element = this.querySelector(`[data-compare-index="${index}"]`);
    if (element) {
      element.setAttribute("data-compare-empty", "true");
      const imageContainer = element.querySelector("[data-compare-image]");
      if (imageContainer) imageContainer.replaceChildren();
      const titleElement = element.querySelector("[data-compare-title]");
      if (titleElement) titleElement.textContent = "";
    }
  }

  setupDeleteProductListeners() {
    this.querySelectorAll("[data-compare-remove]").forEach((button) => {
      button.addEventListener("click", () => {
        const btnIndex = getNumber(button.dataset.compareRemove);
        const products = Array.from(selectedProducts.values());
        const product = products[btnIndex];

        if (product) {
          updateCheckboxInput(product.id, false);
          selectedProducts.delete(product.id);
          sectionCache.delete(product.id);
          setLocalStorage();
          this.updateDrawer();
        }
      });
    });
  }

  clearAll() {
    Array.from(selectedProducts.values()).forEach((product) => {
      updateCheckboxInput(product.id, false);
    });
    selectedProducts.clear();
    sectionCache.clear();
    setLocalStorage();
    this.updateDrawer();
    updateCompareTable();
  }
}

if (!customElements.get("product-compare-drawer")) {
  customElements.define("product-compare-drawer", ProductCompareDrawer);
}

class ProductCompareTable extends HTMLElement {
  constructor() {
    super();
    this.limit = getNumber(this.dataset.compareLimit);
    this.closeButton = this.parentElement.querySelector(".modal-close-button");
  }

  connectedCallback() {
    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => {
        navbarToggle(false);
      });
    }
    this.setupRemoveProductListeners();
  }

  updateProduct() {
    const products = Array.from(selectedProducts.values());

    if (selectedProducts.size === 0 && this.closeButton) {
      this.closeButton.click();
      navbarToggle(false);
    }

    for (let i = 0; i < this.limit; i++) {
      const product = products[i];
      if (product) {
        this.populateProduct(i, product);
      } else {
        this.removeProduct(i);
      }
    }
  }

  populateProduct(i, product) {
    this.updateCell("compare-products", i, (cell) => {
      const newImage = createImageElement(product.image, product.title);
      this.updateChildren("image", cell, newImage);

      const titleElement = cell.querySelector("[data-compare-title]");
      if (titleElement) {
        const titleLink = document.createElement("a");
        titleLink.setAttribute("data-compare-title", i);
        titleLink.href = `/products/${product.handle}`;
        titleLink.target = "_blank";
        titleLink.setAttribute("aria-label", product.title);
        titleLink.className = "compare-title cursor-pointer";
        titleLink.textContent = product.title;
        titleElement.replaceWith(titleLink);
      }

      this.updateTextContent("price", cell, product.price);
      const removeButton = cell.querySelector("[data-compare-remove]");
      if (removeButton) removeButton.classList.remove("hidden");
    });

    this.updateCell("ratings", i, (cell, name) => {
      const newRating = createBvInlineRating(product.id);
      this.updateChildren(name, cell, newRating);
    });

    this.updateCell("formulated", i, (cell, name) => {
      this.updateTextContent(name, cell, product.formulated);
    });

    this.updateCell("features", i, (cell, name) => {
      this.updateTextContent(name, cell, product.features);
    });

    this.updateCell("finish", i, (cell, name) => {
      this.updateTextContent(name, cell, product.finish);
    });

    this.updateCell("skin-type", i, (cell, name) => {
      this.updateTextContent(name, cell, product.skinType);
    });

    this.updateCell("what-it-is", i, (cell, name) => {
      const section = sectionCache.get(product.id);
      this.updateHTMLContent(name, cell, section.whatItIs);
    });

    this.updateCell("what-it-does", i, (cell, name) => {
      const section = sectionCache.get(product.id);
      this.updateHTMLContent(name, cell, section.whatItDoes);
    });

    this.updateCell("shade-range", i, (cell, name) => {
      this.updateTextContent(name, cell, product.shadeRange);
    });

    this.updateCell("action", i, (cell) => {
      const compareActionElement = cell.querySelector("[data-compare-action]");
      if (compareActionElement) {
        const actionLink = document.createElement("a");
        actionLink.setAttribute("data-compare-title", i);
        actionLink.href = `/products/${product.handle}`;
        actionLink.target = "_blank";
        actionLink.setAttribute("aria-label", product.title);
        actionLink.className =
          "shop-now btn btn-skin demo quick-add__submit button button--full-width button--secondary";
        actionLink.textContent = "Shop Now";
        compareActionElement.replaceChildren(actionLink);
      }
    });
  }

  removeProduct(i) {
    this.updateCell("compare-products", i, (cell) => {
      const placeholder = createImagePlaceholder(i, () =>
        this.closeButton.click(),
      );
      this.updateChildren("image", cell, placeholder);
      const titleElement = cell.querySelector("[data-compare-title]");
      if (titleElement) {
        const title = document.createElement("div");
        title.setAttribute("data-compare-title", i);
        title.className = "compare-title";
        title.textContent = "Add a product";
        titleElement.replaceWith(title);
      }
      this.updateTextContent("price", cell, "$00.00");
      const removeButton = cell.querySelector("[data-compare-remove]");
      if (removeButton) removeButton.classList.add("hidden");
    });

    this.updateCell("ratings", i, (cell, name) => {
      this.updateHTMLContent(name, cell, "");
    });

    this.updateCell("formulated", i, (cell, name) => {
      this.updateTextContent(name, cell, "");
    });

    this.updateCell("features", i, (cell, name) => {
      this.updateTextContent(name, cell, "");
    });

    this.updateCell("finish", i, (cell, name) => {
      this.updateTextContent(name, cell, "");
    });

    this.updateCell("skin-type", i, (cell, name) => {
      this.updateTextContent(name, cell, "");
    });

    this.updateCell("what-it-is", i, (cell, name) => {
      this.updateHTMLContent(name, cell, "");
    });

    this.updateCell("what-it-does", i, (cell, name) => {
      this.updateHTMLContent(name, cell, "");
    });

    this.updateCell("shade-range", i, (cell, name) => {
      this.updateTextContent(name, cell, "");
    });

    this.updateCell("action", i, (cell) => {
      this.updateChildren("action", cell, "");
    });
  }

  updateCell(name, index, callback) {
    const element = this.querySelector(
      `[data-compare-cell="${name}"][data-compare-index="${index}"]`,
    );
    if (!element) return;
    callback(element, name);
  }

  updateChildren(name, element, children) {
    if (!element) return;
    const innerCell = element.querySelector(`[data-compare-${name}]`);
    if (innerCell) innerCell.replaceChildren(children || []);
  }

  updateTextContent(name, element, data) {
    if (!element) return;
    const innerCell = element.querySelector(`[data-compare-${name}]`);
    if (innerCell) innerCell.textContent = data;
  }

  updateHTMLContent(name, element, data) {
    if (!element) return;
    const innerCell = element.querySelector(`[data-compare-${name}]`);
    if (innerCell) innerCell.innerHTML = data || "";
  }

  setupRemoveProductListeners() {
    this.querySelectorAll("[data-compare-remove]").forEach((button) => {
      button.addEventListener("click", () => {
        const btnIndex = getNumber(button.dataset.compareRemove);
        const products = Array.from(selectedProducts.values());
        const product = products[btnIndex];

        if (product) {
          updateCheckboxInput(product.id, false);
          selectedProducts.delete(product.id);
          sectionCache.delete(product.id);
          setLocalStorage();
          this.updateProduct();
          updateCompareDrawer();
        }
      });
    });
  }
}

if (!customElements.get("product-compare-table")) {
  customElements.define("product-compare-table", ProductCompareTable);
}
